import { Component, Inject, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { Observable } from "rxjs";
import { ChecklistQuestion } from "@stockcare-models/ChecklistQuestion";
import { WhoChecklist } from "@stockcare-models/WhoChecklist";
import { WhoOptionsYesNo } from "@stockcare-models/WhoOptionsYesNo";
import { WhoOptionsYesNoNA } from "@stockcare-models/WhoOptionsYesNoNA";
import { WhoOptionsTrueFalse } from "@stockcare-models/WhoOptionsTrueFalse";
import { WhoOptionsYesNoNone } from "@stockcare-models/WhoOptionsYesNoNone";
import { WhoOptionsNonAdult } from "@stockcare-models/WhoOptionsNonAdult";
import { WhoOptionsSurgery } from "@stockcare-models/WhoOptionsSurgery";
import { WhoOptionsAirway } from "@stockcare-models/WhoOptionsAirway";
import { WhoQuestionType, getQuestionType, isArrayQuestion, getWhoChecklistPropertyName, shouldShowQuestion } from "@stockcare-models/WhoQuestionTypeMap";
import { WhoChecklistService } from "@stockcare-services/who-checklist-service/who-checklist.service";
import { TheatrePatient } from "@stockcare-storage/theatre-patient/theatre-patient";
import { NotificationService } from "@stockcare-services/notification-service/notification.service";
import { DeviceService } from "@stockcare-services/devices-service/devices.service";
import { SettingsService } from "@stockcare-services/settings-service/settings.service";
import { Settings } from "@stockcare-helpers/settings";
import { TheatrePatientStorageService } from "@stockcare-storage/theatre-patient/theatre-patient-storage.service";
import { TheatreDoctorListComponent } from "../theatre-doctor-list/theatre-doctor-list.component";
import { TheatreAnaesthetistListComponent } from "../theatre-anaesthetist-list/theatre-anaesthetist-list.component";
import { DoctorStorageService } from "@stockcare-storage/doctor/doctor-storage.service";
import { WhoChecklistStorageService } from "@stockcare-storage/checklist/who-checklist-storage.service";
import { ChecklistProgressStorageService } from "@stockcare-storage/checklist/checklist-progress-storage.service";
import { KeyboardTemplate } from "@stockcare-components/custom-keyboard/keyboard-templates";
import { CustomKeyboardComponent } from "@stockcare-components/custom-keyboard/custom-keyboard.component";

export interface InfoContainerData {
  id: string;
  type: 'patient' | 'doctor' | 'anaesthetist' | 'add-doctor' | 'add-anaesthetist';
  mainIcon: string;
  headerText: string;
  subtitleText?: string;
  isAddType?: boolean;
  actionIcons?: Array<{ icon: string; action: string; }>;
  data?: any; // For storing doctor/anaesthetist specific data
}

@Component({
  standalone: false,
  templateUrl: './surgical-safety-checklist-dialog.component.html',
  styleUrls: ['./surgical-safety-checklist-dialog.component.scss'],

})
export class SurgicalSafetyChecklistDialogComponent implements OnInit, OnDestroy {
   @ViewChild('customKeyboard') public customKeyboard: CustomKeyboardComponent;
  public standardTemplate = KeyboardTemplate.StandardWithSpace;
  theatrePatient: TheatrePatient;
  completedStep: number;
  totalSteps: number;
  currentQuestion: string = '';
  currentQuestionOptions: string[] = [];
  questions: ChecklistQuestion[] = [];
  originalQuestions: ChecklistQuestion[] = [];
  whoChecklist: WhoChecklist = new WhoChecklist();
  freeTextResponse: string = '';
  selectedMultipleOptions: string[] = [];
  selectedMultipleIndices: number[] = [];
  selectedRadioOption: string = '';
  selectedRadioIndex: number = -1;
  selectedStandardOption: string = '';
  selectedStandardIndex: number = -1;
  currentQuestionId: string = '';
  isSaving: boolean = false;
  selectedStep: number = 0;
  maxDoctors: number;
  maxAnaesthetists: number;
  isTheatreBackOfficeDevice: boolean = false;
  infoContainers: InfoContainerData[] = [];
  selectedDoctors: any[] = [];
  selectedAnaesthetists: any[] = [];
  dialogConfig: MatDialogConfig;
  hasChanges: boolean = false;
  theatrePatientHasChanges: boolean = false;

  private whoChecklistBackup: WhoChecklist = new WhoChecklist();
  private selectedDoctorsBackup: any[] = [];
  private selectedAnaesthetistsBackup: any[] = [];
  private theatrePatientBackup: TheatrePatient;

  enableCustomKeyboard: boolean = false;
  isCommentFieldFocused: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<SurgicalSafetyChecklistDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private whoChecklistService: WhoChecklistService,
    private readonly notify: NotificationService,
    private readonly deviceService: DeviceService,
    private readonly settingService: SettingsService,
    private readonly modalService: MatDialog,
    private readonly theatrePatientStorageService: TheatrePatientStorageService,
    private readonly doctorStorageService: DoctorStorageService,
    private readonly checklistProgressStorageService: ChecklistProgressStorageService,
    private readonly whoChecklistStorageService: WhoChecklistStorageService
  ) {
    this.theatrePatient = data.theatrePatient;
    this.selectedStep = data.currentStep + 1;
    this.completedStep = data.currentStep;
    this.totalSteps = Math.max(data.totalSteps, 1);
    this.whoChecklist.patientTheatreVisitId = this.theatrePatient.Id;
    this.theatrePatientHasChanges = data.theatrePatientHasChanges || false;
  }


  async ngOnInit(): Promise<void> {
    this.isTheatreBackOfficeDevice = this.deviceService.isTheatreBackOfficeDevice();
    this.maxDoctors = this.settingService.getSettingNumber(Settings.THEATRE_MAX_DOCTORS);
    this.maxAnaesthetists = this.settingService.getSettingNumber(Settings.THEATRE_MAX_ANAESTHETISTS);

    await this.loadExistingDoctorsAndAnaesthetists();
    this.ensureAnswersAreLoaded();

    this.dialogConfig = {
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: 'calc(100vw - 10px)',
      height: 'calc(100vh - 65px)',
      position: {
        bottom: '5px',
        left: '5px',
        right: '5px',
        top: '60px',
      },
    };

    this.createBackup();
    this.enableCustomKeyboard = this.settingService.enableCustomKeyboard();
  }

  private createBackup(): void {
    this.whoChecklistBackup = JSON.parse(JSON.stringify(this.whoChecklist));

    this.selectedDoctorsBackup = JSON.parse(JSON.stringify(this.selectedDoctors));
    this.selectedAnaesthetistsBackup = JSON.parse(JSON.stringify(this.selectedAnaesthetists));

    this.theatrePatientBackup = JSON.parse(JSON.stringify(this.theatrePatient));
  }

  private restoreFromBackup(): void {
    this.whoChecklist = JSON.parse(JSON.stringify(this.whoChecklistBackup));

    this.selectedDoctors = JSON.parse(JSON.stringify(this.selectedDoctorsBackup));
    this.selectedAnaesthetists = JSON.parse(JSON.stringify(this.selectedAnaesthetistsBackup));

    this.theatrePatient = JSON.parse(JSON.stringify(this.theatrePatientBackup));

    this.hasChanges = false;

    this.initializeInfoContainers();

    this.calculateProgressFromSavedAnswers();
    this.updateCurrentQuestion();
  }

  private async loadExistingDoctorsAndAnaesthetists(): Promise<void> {
    try {
      if (this.theatrePatientHasChanges) {
        await new Promise<void>((resolve) => {
          this.theatrePatientStorageService.getPatientById(this.theatrePatient.Id).subscribe(async (updatedPatient) => {
            if (updatedPatient) {
              this.theatrePatient = updatedPatient;

              if (this.theatrePatient.DoctorIds && this.theatrePatient.DoctorIds.length > 0) {
                const doctors = await this.doctorStorageService.getDoctorsByIds(this.theatrePatient.DoctorIds);
                this.selectedDoctors = doctors.map(doctor => ({
                  id: doctor.Id,
                  name: doctor.Name
                }));
              }

              if (this.theatrePatient.AnaesthetistIds && this.theatrePatient.AnaesthetistIds.length > 0) {
                const anaesthetists = await this.doctorStorageService.getAnaesthetistsByIds(this.theatrePatient.AnaesthetistIds);
                this.selectedAnaesthetists = anaesthetists.map(anaesthetist => ({
                  id: anaesthetist.Id,
                  name: anaesthetist.Name
                }));
              }
            }
            resolve();
          });
        });
      } else {
        // Load from current theatre patient when no changes pending
        if (this.theatrePatient.DoctorIds && this.theatrePatient.DoctorIds.length > 0) {
          const doctors = await this.doctorStorageService.getDoctorsByIds(this.theatrePatient.DoctorIds);
          this.selectedDoctors = doctors.map(doctor => ({
            id: doctor.Id,
            name: doctor.Name
          }));
        }

        if (this.theatrePatient.AnaesthetistIds && this.theatrePatient.AnaesthetistIds.length > 0) {
          const anaesthetists = await this.doctorStorageService.getAnaesthetistsByIds(this.theatrePatient.AnaesthetistIds);
          this.selectedAnaesthetists = anaesthetists.map(anaesthetist => ({
            id: anaesthetist.Id,
            name: anaesthetist.Name
          }));
        }
      }

      this.initializeInfoContainers();
    } catch (error) {
      console.error('Error loading existing doctors and anaesthetists:', error);
      this.initializeInfoContainers();
    }
  }

  ensureAnswersAreLoaded() {
    if (!this.theatrePatient || !this.theatrePatient.Id) {
      return;
    }

    this.whoChecklistService.ensureChecklistAnswersAreLoaded(this.theatrePatient.Id)
      .subscribe(
        () => {
          this.loadAnsweredQuestions().subscribe(
            () => {
              this.getChecklistQuestions();
            },
            (error) => {
              console.error('Error loading saved checklist answers:', error);
              this.getChecklistQuestions(); // Still load questions even if answers fail
            });
        },
        () => {
          this.loadAnsweredQuestions().subscribe(
            () => {
              this.getChecklistQuestions();
            },
            (error) => {
              console.error('Error loading saved checklist answers:', error);
              this.getChecklistQuestions(); // Still load questions even if answers fail
            });
        }
      );
  }

  loadAnsweredQuestions(): Observable<void> {
    return new Observable((observer) => {
      this.whoChecklistStorageService.getWhoChecklistById(this.theatrePatient.Id).subscribe(
        (savedAnswers) => {
          try {
            if (savedAnswers && typeof savedAnswers === 'object') {
              const keys = Object.keys(savedAnswers);
              const isNewChecklist = keys.length === 1 &&
                keys[0].toLowerCase() === 'patienttheatrevisitid' &&
                savedAnswers[keys[0]] === 0;

              if (!isNewChecklist) {
                let hasValidAnswers = false;

                Object.keys(savedAnswers).forEach(key => {
                  if (key !== 'patientTheatreVisitId' &&
                    key.toLowerCase() !== 'patienttheatrevisitid') {
                    const value = savedAnswers[key];

                    if (value !== null && value !== undefined && value !== '') {
                      (this.whoChecklist as any)[key] = value;
                      hasValidAnswers = true;
                    }
                  }
                });
              }
            }

            this.whoChecklist.patientTheatreVisitId = this.theatrePatient.Id;
            observer.next();
            observer.complete();
          } catch (error) {
            console.error('Error processing saved checklist answers:', error);
            this.whoChecklist.patientTheatreVisitId = this.theatrePatient.Id;
            observer.next();
            observer.complete();
          }
        },
        (error) => {
          console.error('Error loading checklist answers from service:', error);
          this.whoChecklist.patientTheatreVisitId = this.theatrePatient.Id;
          observer.next();
          observer.complete();
        });
    });
  }

  private initializeInfoContainers(): void {
    this.infoContainers = [];

    this.infoContainers.push({
      id: 'patient',
      type: 'patient',
      mainIcon: 'checklist-patient',
      headerText: 'Case Number',
      subtitleText: this.theatrePatient.CaseNumber,
      actionIcons: []
    });

    this.selectedDoctors.forEach((doctor, index) => {
      this.infoContainers.push({
        id: `doctor-${index}`,
        type: 'doctor',
        mainIcon: 'checklist-doctor',
        headerText: 'Doctor',
        subtitleText: doctor.name || 'Doctor',
        actionIcons: [
          { icon: 'checklist-edit', action: `edit-doctor-${index}` },
          { icon: 'checklist-delete', action: `delete-doctor-${index}` }
        ],
        data: doctor
      });
    });

    if (this.selectedDoctors.length < this.maxDoctors) {
      this.infoContainers.push({
        id: 'add-doctor',
        type: 'add-doctor',
        mainIcon: 'checklist-doctor',
        headerText: 'Add A Doctor',
        isAddType: true,
        actionIcons: [
          { icon: 'checklist-add', action: 'add-doctor' }
        ]
      });
    }

    this.selectedAnaesthetists.forEach((anaesthetist, index) => {
      this.infoContainers.push({
        id: `anaesthetist-${index}`,
        type: 'anaesthetist',
        mainIcon: 'checklist-syringe',
        headerText: 'Anaesthetist',
        subtitleText: anaesthetist.name,
        actionIcons: [
          { icon: 'checklist-edit', action: `edit-anaesthetist-${index}` },
          { icon: 'checklist-delete', action: `delete-anaesthetist-${index}` }
        ],
        data: anaesthetist
      });
    });

    if (this.selectedAnaesthetists.length < this.maxAnaesthetists) {
      this.infoContainers.push({
        id: 'add-anaesthetist',
        type: 'add-anaesthetist',
        mainIcon: 'checklist-syringe',
        headerText: 'Add An Anaesthetist',
        isAddType: true,
        actionIcons: [
          { icon: 'checklist-add', action: 'add-anaesthetist' }
        ]
      });
    }
  }

  nextQuestion() {
    if (!this.isCurrentQuestionAnswered()) {
      this.notify.error('Current question must be answered before proceeding');
      return;
    }

    const currentQ = this.questions[this.selectedStep - 1];

    if (this.isMultipleSelectionQuestion() && this.selectedMultipleIndices.length > 0) {
      this.syncMultipleSelections();
      const responseValue = this.selectedMultipleOptions.join(',');
      this.saveQuestionResponse(currentQ.id, responseValue);
    } else if (this.isFreeTextQuestion() && this.freeTextResponse.trim()) {
      this.saveQuestionResponse(currentQ.id, this.freeTextResponse);
    } else if (this.isRadioButtonQuestion() && this.selectedRadioOption) {
      this.saveQuestionResponse(currentQ.id, this.selectedRadioOption);
    } else if (this.isStandardButtonQuestion() && this.selectedStandardOption) {
      this.saveQuestionResponse(currentQ.id, this.selectedStandardOption);
    }

    if (this.selectedStep < this.totalSteps) {
      if (this.completedStep < this.selectedStep) {
        this.completedStep = this.selectedStep;
      }
      this.selectedStep++;

      this.updateCurrentQuestion();
    }
  }

  previousQuestion() {
    if (this.selectedStep > 1) {
      this.selectedStep--;

      this.updateCurrentQuestion();
    }
  }

  private autoAdvanceToNextQuestion(): void {
    setTimeout(() => {
      this.isSaving = false;
      if (this.selectedStep < this.totalSteps) {
        if (this.completedStep < this.selectedStep) {
          this.completedStep = this.selectedStep;
        }
        this.selectedStep++;
        this.updateCurrentQuestion();
      }
    }, 300);
  }

  private updateCurrentQuestion() {
    if (this.questions.length > 0) {
      if (this.selectedStep < 1) {
        this.selectedStep = 1;
      } else if (this.selectedStep > this.questions.length) {
        this.selectedStep = this.questions.length;
      }

      const currentQ = this.questions[this.selectedStep - 1];
      if (currentQ) {
        this.currentQuestion = currentQ.questionText;
        this.currentQuestionId = currentQ.id;
        this.currentQuestionOptions = this.getQuestionOptions(currentQ.id);

        this.selectedMultipleOptions = [];
        this.selectedMultipleIndices = [];
        this.selectedRadioOption = '';
        this.selectedStandardOption = '';
        this.freeTextResponse = '';
        this.loadExistingResponse(currentQ.id);

        if (this.isMultipleSelectionQuestion()) {
          this.syncMultipleSelections();
        }
      } else {
        console.error('Current question is undefined. selectedStep:', this.selectedStep, 'questions.length:', this.questions.length);
      }
    }
  }

  private getQuestionOptions(questionId: string): string[] {
    const questionType = getQuestionType(questionId);
    return this.getOptionsForQuestionType(questionType);
  }

  private getOptionsForQuestionType(questionType: WhoQuestionType): string[] {
    switch (questionType) {
      case WhoQuestionType.YesNo:
        return this.getYesNoOptions();
      case WhoQuestionType.YesNoNA:
        return this.getYesNoNAOptions();
      case WhoQuestionType.TrueFalse:
        return this.getTrueFalseOptions();
      case WhoQuestionType.YesNoNone:
        return this.getYesNoNoneOptions();
      case WhoQuestionType.NonAdult:
        return this.getNonAdultOptions();
      case WhoQuestionType.Surgery:
        return this.getSurgeryOptions();
      case WhoQuestionType.Airway:
        return this.getAirwayOptions();
      case WhoQuestionType.FreeText:
        return ['Free Text Input'];
      default:
        return this.getYesNoOptions();
    }
  }

  private getYesNoOptions(): string[] {
    return Object.keys(WhoOptionsYesNo).filter(key => isNaN(Number(key)))
      .map(key => key.replace(/_/g, ' '));
  }

  private getYesNoNAOptions(): string[] {
    return Object.keys(WhoOptionsYesNoNA).filter(key => isNaN(Number(key)))
      .map(key => {
        if (key === 'Not_Applicable') {
          // Check if we're displaying options for q20
          if (this.currentQuestionId === '20') {
            return 'Not Applicable';
          } else {
            return 'NONE APPLY';
          }
        }
        return key.replace(/_/g, ' ');
      });
  }

  private getTrueFalseOptions(): string[] {
    return Object.keys(WhoOptionsTrueFalse).filter(key => isNaN(Number(key)))
      .map(key => key.replace(/_/g, ' '));
  }

  private getYesNoNoneOptions(): string[] {
    return Object.keys(WhoOptionsYesNoNone).filter(key => isNaN(Number(key)))
      .map(key => key.replace(/_/g, ' '));
  }

  private getNonAdultOptions(): string[] {
    return Object.keys(WhoOptionsNonAdult).filter(key => isNaN(Number(key)))
      .map(key => key.replace(/_/g, ' '));
  }

  private getSurgeryOptions(): string[] {
    return Object.keys(WhoOptionsSurgery).filter(key => isNaN(Number(key)))
      .map(key => key.replace(/_/g, ' '));
  }

  private getAirwayOptions(): string[] {
    return Object.keys(WhoOptionsAirway).filter(key => isNaN(Number(key)))
      .map(key => key.replace(/_/g, ' '));
  }

  selectOption(option: string) {
    if (this.isSaving) {
      return;
    }
    if (this.isMultipleSelectionQuestion()) {
      const optionIndex = this.currentQuestionOptions.indexOf(option);
      const isCurrentlySelected = optionIndex !== -1 && this.selectedMultipleIndices.includes(optionIndex);
      this.toggleMultipleOption(option, { target: { checked: !isCurrentlySelected } } as any);
    } else if (this.isRadioButtonQuestion()) {
      this.selectRadioOption(option);
    } else {
      const currentQ = this.questions[this.selectedStep - 1];
      this.selectedStandardOption = option;
      this.saveQuestionResponse(currentQ.id, option);
      this.isSaving = true;
      this.autoAdvanceToNextQuestion();
    }
  }

  selectRadioOption(option: string): void {
    if (this.isSaving) {
      return;
    }
    this.selectedRadioOption = option;
    const currentQ = this.questions[this.selectedStep - 1];
    this.saveQuestionResponse(currentQ.id, option);
    this.isSaving = true;
    this.autoAdvanceToNextQuestion();
  }

  isMultipleSelectionQuestion(): boolean {
    return isArrayQuestion(this.currentQuestionId);
  }

  isFreeTextQuestion(): boolean {
    return false;
  }

  isRadioButtonQuestion(): boolean {
    const questionType = getQuestionType(this.currentQuestionId);
    return (questionType === WhoQuestionType.Airway ||
      questionType === WhoQuestionType.NonAdult ||
      questionType === WhoQuestionType.Surgery) &&
      !isArrayQuestion(this.currentQuestionId);
  }

  isStandardButtonQuestion(): boolean {
    return !this.isMultipleSelectionQuestion() &&
      !this.isFreeTextQuestion() &&
      !this.isRadioButtonQuestion();
  }

  isCurrentQuestionAnswered(): boolean {
    if (this.selectedStep <= this.completedStep) {
      return true;
    }

    const currentQ = this.questions[this.selectedStep - 1];
    if (currentQ) {
      const propertyName = getWhoChecklistPropertyName(currentQ.id);
      const existingValue = (this.whoChecklist as any)[propertyName];

      if (existingValue !== null && existingValue !== undefined && existingValue !== '') {
        return true;
      }
    }

    if (this.isMultipleSelectionQuestion()) {
      return this.selectedMultipleIndices.length > 0;
    } else if (this.isRadioButtonQuestion()) {
      return this.selectedRadioOption !== '';
    } else if (this.isFreeTextQuestion()) {
      return this.freeTextResponse.trim() !== '';
    } else if (this.isStandardButtonQuestion()) {
      return this.selectedStandardOption !== '';
    }
    return false;
  }

  isOptionSelected(option: string): boolean {
    if (this.isMultipleSelectionQuestion()) {
      const optionIndex = this.currentQuestionOptions.indexOf(option);
      return optionIndex !== -1 && this.selectedMultipleIndices.includes(optionIndex);
    } else if (this.isRadioButtonQuestion()) {
      return this.selectedRadioOption === option;
    } else if (this.isStandardButtonQuestion()) {
      return this.selectedStandardOption === option;
    }
    return false;
  }

  toggleMultipleOption(option: string, event: any): void {
    const optionIndex = this.currentQuestionOptions.indexOf(option);

    if (optionIndex === -1) {
      console.error('Option not found in current question options:', option);
      return;
    }

    if (event.target.checked) {
      if (!this.selectedMultipleIndices.includes(optionIndex)) {
        this.selectedMultipleIndices.push(optionIndex);
      }
    } else {
      const originalLength = this.selectedMultipleIndices.length;
      this.selectedMultipleIndices = this.selectedMultipleIndices.filter(idx => idx !== optionIndex);
    }

    this.syncMultipleSelections();
  }

  onFreeTextChange(event: any): void {}
  private syncMultipleSelections(): void {
    this.selectedMultipleOptions = this.selectedMultipleIndices
      .filter(index => index >= 0 && index < this.currentQuestionOptions.length)
      .map(index => this.currentQuestionOptions[index]);
  }

  private loadExistingResponse(questionId: string): void {
    const propertyName = getWhoChecklistPropertyName(questionId);
    let existingValue = (this.whoChecklist as any)[propertyName];

    if (existingValue === null || existingValue === undefined) {

      var variation = `q${questionId}Options`
      const value = (this.whoChecklist as any)[variation];
      if (value !== null && value !== undefined) {
        existingValue = value;
      }
    }

    if (existingValue !== null && existingValue !== undefined) {
      if (this.isMultipleSelectionQuestion()) {

        if (Array.isArray(existingValue)) {
          const convertedIndices = existingValue.map((v, idx) => {
            const convertedIndex = this.convertEnumToIndex(v, this.currentQuestionId);
            return convertedIndex;
          });

          this.selectedMultipleIndices = convertedIndices.filter(idx => idx !== -1);
        } else {
          const rawSelections = existingValue.toString().split(',')
            .map(v => v.trim())
            .filter(v => v);

          const convertedIndices = rawSelections.map((v, idx) => {
            let resultIndex = -1;
            if (v.match(/^\d+$/)) {
              const index = parseInt(v, 10);
              resultIndex = index >= 0 && index < this.currentQuestionOptions.length ? index : -1;
            } else {
              const displayText = this.enumValueToDisplayText(v);
              resultIndex = this.currentQuestionOptions.indexOf(displayText);
            }
            return resultIndex;
          });

          this.selectedMultipleIndices = convertedIndices.filter(idx => idx !== -1);
        }

        this.syncMultipleSelections();
      } else if (this.isRadioButtonQuestion()) {
        const convertedIndex = this.convertEnumToIndex(existingValue, this.currentQuestionId);

        if (convertedIndex !== -1 && convertedIndex < this.currentQuestionOptions.length) {
          this.selectedRadioOption = this.currentQuestionOptions[convertedIndex];
        } else {
          this.selectedRadioOption = this.enumValueToDisplayText(existingValue);
        }
    } else if (this.isFreeTextQuestion()) {
        this.freeTextResponse = existingValue.toString();
    } else if (this.isStandardButtonQuestion()) {
        const convertedIndex = this.convertEnumToIndex(existingValue, this.currentQuestionId);

        if (convertedIndex !== -1 && convertedIndex < this.currentQuestionOptions.length) {
          this.selectedStandardOption = this.currentQuestionOptions[convertedIndex];
        } else {
          this.selectedStandardOption = this.enumValueToDisplayText(existingValue);
        }
      }
    }
  }

  private enumValueToDisplayText(enumValue: any): string {
    if (typeof enumValue === 'string') {
      return enumValue.replace(/_/g, ' ');
    }
    return enumValue.toString().replace(/_/g, ' ');
  }

  private convertEnumToIndex(enumValue: any, questionId: string): number {
    const displayText = this.enumValueToDisplayText(enumValue);
    const index = this.currentQuestionOptions.indexOf(displayText);

    if (index === -1) {
      if (typeof enumValue === 'number' || (typeof enumValue === 'string' && enumValue.match(/^\d+$/))) {
        const numValue = typeof enumValue === 'number' ? enumValue : parseInt(enumValue, 10);

        const adjustedIndex = numValue - 1;

        if (adjustedIndex >= 0 && adjustedIndex < this.currentQuestionOptions.length) {
          return adjustedIndex;
        }
      }
    }
    return index;
  }

  private saveQuestionResponse(questionId: string, response: string) {
    const propertyName = getWhoChecklistPropertyName(questionId);
    const questionType = getQuestionType(questionId);
    const mappedValue = this.mapResponseToEnum(questionType, response, isArrayQuestion(questionId));

    // Dynamically set the property on the whoChecklist object
    (this.whoChecklist as any)[propertyName] = mappedValue;

    // Create backup after every 5 questions answered (optional)
    if (this.completedStep > 0 && this.completedStep % 5 === 0) {
      this.createBackup();
    }

    // Re-filter questions when answers change to handle conditional questions
    this.reFilterQuestionsOnAnswerChange(questionId);
  }

  private reFilterQuestionsOnAnswerChange(changedQuestionId: string): void {
    // Only re-filter if the changed question affects conditional logic
    // Currently, only question 6 affects question 6a visibility
    if (changedQuestionId !== '6') {
      return;
    }

    // Store current position info
    const currentQuestionId = this.questions[this.selectedStep - 1]?.id;

    // Special handling for question 6 changes
    const q6PropertyName = getWhoChecklistPropertyName('6');
    const q6Value = (this.whoChecklist as any)[q6PropertyName];

    // Re-filter the questions based on current answers
    this.filterConditionalQuestions();

    // Find the new position of the current question after filtering
    const newPosition = this.questions.findIndex(q => q.id === currentQuestionId);

    if (newPosition !== -1) {
      this.selectedStep = newPosition + 1;
    } else {
      // Current question was filtered out, move to previous valid question
      if (this.selectedStep > this.questions.length) {
        this.selectedStep = this.questions.length;
      }
    }

    // Update the UI to reflect the new question list
    this.updateCurrentQuestion();
  }

  private mapResponseToEnum(questionType: WhoQuestionType, response: string, isArray: boolean = false): any {
    switch (questionType) {
      case WhoQuestionType.YesNo:
        return this.mapToYesNo(response);
      case WhoQuestionType.YesNoNA:
        return this.mapToYesNoNA(response);
      case WhoQuestionType.TrueFalse:
        return this.mapToTrueFalse(response);
      case WhoQuestionType.YesNoNone:
        return this.mapToYesNoNone(response);
      case WhoQuestionType.NonAdult:
        if (isArray) {
          // Handle comma-separated multiple selections and remove duplicates
          const selections = response.split(',')
            .map(s => s.trim())
            .filter(s => s)
            .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates
          return selections.map(sel => this.mapToNonAdult(sel));
        }
        return this.mapToNonAdult(response);
      case WhoQuestionType.Surgery:
        if (isArray) {
          // Handle comma-separated multiple selections and remove duplicates
          const selections = response.split(',')
            .map(s => s.trim())
            .filter(s => s)
            .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates
          return selections.map(sel => this.mapToSurgery(sel));
        }
        return this.mapToSurgery(response);
      case WhoQuestionType.Airway:
        return this.mapToAirway(response);
      case WhoQuestionType.FreeText:
        return response;
      default:
        return this.mapToYesNo(response);
    }
  }

  private mapToYesNo(response: string): WhoOptionsYesNo {
    const enumKey = response.replace(/ /g, '_');
    return response === 'Yes' ? WhoOptionsYesNo.Yes : WhoOptionsYesNo.No;
  }

  private mapToYesNoNA(response: string): WhoOptionsYesNoNA {
    switch (response) {
      case 'Yes': return WhoOptionsYesNoNA.Yes;
      case 'No': return WhoOptionsYesNoNA.No;
      case 'N/A': return WhoOptionsYesNoNA.Not_Applicable;
      case 'NONE APPLY': return WhoOptionsYesNoNA.Not_Applicable;
      case 'Not Applicable': return WhoOptionsYesNoNA.Not_Applicable;
      default: return WhoOptionsYesNoNA.No;
    }
  }

  private mapToTrueFalse(response: string): WhoOptionsTrueFalse {
    return response === 'True' ? WhoOptionsTrueFalse.True : WhoOptionsTrueFalse.False;
  }

  private mapToYesNoNone(response: string): WhoOptionsYesNoNone {
    switch (response) {
      case 'Yes': return WhoOptionsYesNoNone.Yes;
      case 'No': return WhoOptionsYesNoNone.No;
      case 'None Of The Above': return WhoOptionsYesNoNone.None_Of_The_Above;
      default: return WhoOptionsYesNoNone.No;
    }
  }

  private mapToNonAdult(response: string): WhoOptionsNonAdult {
    // Convert display text back to enum key format
    const enumKey = response.replace(/ /g, '_');
    return (WhoOptionsNonAdult as any)[enumKey] || response as any;
  }

  private mapToSurgery(response: string): WhoOptionsSurgery {
    // Convert display text back to enum key format
    const enumKey = response.replace(/ /g, '_');
    return (WhoOptionsSurgery as any)[enumKey] || response as any;
  }

  private mapToAirway(response: string): WhoOptionsAirway {
    // Convert display text back to enum key format
    const enumKey = response.replace(/ /g, '_');
    return (WhoOptionsAirway as any)[enumKey] || response as any;
  }

  saveChecklist() {

    // Save current multiple selection question if it has selections
    if (this.isMultipleSelectionQuestion() && this.selectedMultipleIndices.length > 0) {
      const currentQ = this.questions[this.selectedStep - 1];
      if (currentQ) {
        this.syncMultipleSelections();
        const responseValue = this.selectedMultipleOptions.join(',');
        this.saveQuestionResponse(currentQ.id, responseValue);
      }
    }

    // Update completed step if current question is answered but user hasn't clicked next yet
    if (this.isCurrentQuestionAnswered() && this.completedStep < this.selectedStep) {
      this.completedStep = this.selectedStep;
    }

    // Always check if there are differences between current selections and theatre patient arrays
    const currentDoctorIds = this.selectedDoctors.map(doctor => doctor.id);
    const currentAnaesthetistIds = this.selectedAnaesthetists.map(anaesthetist => anaesthetist.id);

    const hasTheatrePatientChanges =
      !this.arraysEqual(currentDoctorIds, this.theatrePatient.DoctorIds || []) ||
      !this.arraysEqual(currentAnaesthetistIds, this.theatrePatient.AnaesthetistIds || []);

    // Save theatre patient changes (doctors and anaesthetists) if there are any
    if (this.hasChanges || hasTheatrePatientChanges) {
      // Update the theatre patient arrays with current selections
      this.theatrePatient.DoctorIds = currentDoctorIds;
      this.theatrePatient.AnaesthetistIds = currentAnaesthetistIds;

      // Preserve the changes state for the dialog result
      const hadTheatrePatientChanges = true;

      this.theatrePatientStorageService.updatePatient(this.theatrePatient).then(() => {
        // Clear the changes flag only after successful save
        this.hasChanges = false;
        // After updating the theatre patient, save the checklist
        this.saveChecklistProgress(hadTheatrePatientChanges);
      }).catch(error => {
        // On error, offer to restore from backup
        const restoreBackup = confirm('Error saving theatre patient data. Would you like to restore from backup?');
        if (restoreBackup) {
          this.restoreFromBackup();
        } else {
          // Still try to save checklist even if patient update fails
          this.saveChecklistProgress(hadTheatrePatientChanges);
        }
      });
    } else {
      // No changes to theatre patient, just save checklist
      this.saveChecklistProgress(false);
    }
  }



  private saveChecklistProgress(hadTheatrePatientChanges: boolean = false) {

    // Calculate the correct progress string using consistent logic
    const currentStep = this.completedStep;
    const totalStepsForProgress = this.totalSteps;
    const progressString = `${currentStep}|${totalStepsForProgress}`;

    // Update the legacy progress string in storage for backward compatibility and offline display
    this.checklistProgressStorageService.addUpdateChecklistProgress(this.theatrePatient.Id, progressString).subscribe(
      () => {
        console.log('Checklist progress updated successfully:', progressString);
      },
      error => {
        console.error('Error updating checklist progress:', error);
      }
    );

    this.whoChecklistService.saveChecklistAnswers(this.whoChecklist).subscribe(async result => {
      if (result) {
        // Update backup after successful save
        this.createBackup();

        this.dialogRef.close({
          currentStep: this.completedStep,
          totalSteps: this.totalSteps,
          hasChanges: hadTheatrePatientChanges,
          updatedTheatrePatient: hadTheatrePatientChanges ? this.theatrePatient : undefined,
          saveSuccess: true // Add flag to indicate successful save
        });
      } else {
        this.notify.error('Error saving checklist answers. Please try again later.');
        this.dialogRef.close({
          currentStep: this.completedStep,
          totalSteps: this.totalSteps,
          hasChanges: hadTheatrePatientChanges,
          updatedTheatrePatient: hadTheatrePatientChanges ? this.theatrePatient : undefined,
          error: 'Error saving checklist answers',
          saveSuccess: false
        });
      }
    },
      // Handle error case, 
      async (error) => {
        this.checklistProgressStorageService.addUpdateChecklistProgress(this.theatrePatient.Id, progressString);
        this.dialogRef.close({
          currentStep: this.completedStep,
          totalSteps: this.totalSteps,
          hasChanges: hadTheatrePatientChanges,
          updatedTheatrePatient: hadTheatrePatientChanges ? this.theatrePatient : undefined,
          error: error,
          saveSuccess: false
        });
      });

  }

  private arraysEqual(a: any[], b: any[]): boolean {
    if (a.length !== b.length) return false;

    // Sort both arrays to compare regardless of order
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();

    return sortedA.every((val, index) => val === sortedB[index]);
  }

  onInfoContainerAction(action: string): void {
    if (action.startsWith('edit-doctor-')) {
      const index = parseInt(action.replace('edit-doctor-', ''));
      this.editDoctor(this.selectedDoctors[index], index);
    } else if (action.startsWith('delete-doctor-')) {
      const index = parseInt(action.replace('delete-doctor-', ''));
      this.removeDoctor(this.selectedDoctors[index], index);
    } else if (action.startsWith('edit-anaesthetist-')) {
      const index = parseInt(action.replace('edit-anaesthetist-', ''));
      this.editAnaesthetist(this.selectedAnaesthetists[index], index);
    } else if (action.startsWith('delete-anaesthetist-')) {
      const index = parseInt(action.replace('delete-anaesthetist-', ''));
      this.removeAnaesthetist(this.selectedAnaesthetists[index], index);
    } else {
      switch (action) {
        case 'add-doctor':
          this.addDoctor();
          break;
        case 'add-anaesthetist':
          this.addAnaesthetist();
          break;
        default:
          console.log('Unknown action:', action);
      }
    }
  }

  private addDoctor(): void {
    if (this.isTheatreBackOfficeDevice) {
      this.notify.error('Edits cannot be made using Theatre Back Office Devices');
      return;
    }

    if (this.selectedDoctors.length < this.maxDoctors) {
      this.dialogConfig.data = null;
      const dialogReference = this.modalService.open(TheatreDoctorListComponent, this.dialogConfig);

      dialogReference.afterClosed().subscribe((doctor) => {
        if (doctor) {
          const newDoctor = {
            id: doctor.Id,
            name: doctor.Name,
          };
          this.selectedDoctors.push(newDoctor);

          if (!this.theatrePatient.DoctorIds) {
            this.theatrePatient.DoctorIds = [];
          }
          this.theatrePatient.DoctorIds.push(doctor.Id);

          this.hasChanges = true;
          this.initializeInfoContainers();
        }
      });
    } else {
      this.notify.warning('Max Number Of Doctors', 'Warning');
    }
  }

  addAnaesthetist() {
    if (this.isTheatreBackOfficeDevice) {
      this.notify.error('Edits cannot be made using Theatre Back Office Devices');
      return;
    }

    if (this.selectedAnaesthetists.length < this.maxAnaesthetists) {
      this.dialogConfig.data = null;
      const dialogReference = this.modalService.open(TheatreAnaesthetistListComponent, this.dialogConfig);

      dialogReference.afterClosed().subscribe((doctor) => {
        if (doctor) {

          const newAnaesthetist = {
            id: doctor.Id,
            name: doctor.Name,
          };
          this.selectedAnaesthetists.push(newAnaesthetist);

          if (!this.theatrePatient.AnaesthetistIds) {
            this.theatrePatient.AnaesthetistIds = [];
          }
          this.theatrePatient.AnaesthetistIds.push(doctor.Id);

          this.hasChanges = true;
          this.initializeInfoContainers();
        }
      });
    } else {
      this.notify.warning('Max Number Of Anaesthetists', 'Warning');
    }
  }

  private editDoctor(doctor: any, index: number): void {
    if (this.isTheatreBackOfficeDevice) {
      this.notify.error('Edits cannot be made using Theatre Back Office Devices');
      return;
    }

    const dialogReference = this.modalService.open(TheatreDoctorListComponent, this.dialogConfig);

    dialogReference.afterClosed().subscribe((newDoctor) => {
      if (newDoctor) {
        this.selectedDoctors[index] = {
          id: newDoctor.Id,
          name: newDoctor.Name,
        };

        if (this.theatrePatient.DoctorIds) {
          const oldDoctorIndex = this.theatrePatient.DoctorIds.findIndex(id => id === doctor.id);
          if (oldDoctorIndex !== -1) {
            this.theatrePatient.DoctorIds[oldDoctorIndex] = newDoctor.Id;
          }
        }

        this.hasChanges = true;
        this.initializeInfoContainers();
      }
    });
  }

  private editAnaesthetist(anaesthetist: any, index: number): void {
    if (this.isTheatreBackOfficeDevice) {
      this.notify.error('Edits cannot be made using Theatre Back Office Devices');
      return;
    }

    const dialogReference = this.modalService.open(TheatreAnaesthetistListComponent, this.dialogConfig);

    dialogReference.afterClosed().subscribe((newAnaesthetist) => {
      if (newAnaesthetist) {
        this.selectedAnaesthetists[index] = {
          id: newAnaesthetist.Id,
          name: newAnaesthetist.Name,
        };

        if (this.theatrePatient.AnaesthetistIds) {
          const oldAnaesthetistIndex = this.theatrePatient.AnaesthetistIds.findIndex(id => id === anaesthetist.id);
          if (oldAnaesthetistIndex !== -1) {
            this.theatrePatient.AnaesthetistIds[oldAnaesthetistIndex] = newAnaesthetist.Id;
          }
        }

        this.hasChanges = true;
        this.initializeInfoContainers();
      }
    });
  }

  private removeDoctor(doctor: any, index: number): void {
    if (this.isTheatreBackOfficeDevice) {
      this.notify.error('Edits cannot be made using Theatre Back Office Devices');
      return;
    }

    this.selectedDoctors.splice(index, 1);

    if (this.theatrePatient.DoctorIds) {
      this.theatrePatient.DoctorIds = this.theatrePatient.DoctorIds.filter(id => id !== doctor.id);
    }

    this.hasChanges = true;
    this.initializeInfoContainers();
  }

  private removeAnaesthetist(anaesthetist: any, index: number): void {
    if (this.isTheatreBackOfficeDevice) {
      this.notify.error('Edits cannot be made using Theatre Back Office Devices');
      return;
    }

    this.selectedAnaesthetists.splice(index, 1);

    if (this.theatrePatient.AnaesthetistIds) {
      this.theatrePatient.AnaesthetistIds = this.theatrePatient.AnaesthetistIds.filter(id => id !== anaesthetist.id);
    }

    this.hasChanges = true;
    this.initializeInfoContainers();
  }

  getChecklistQuestions() {
    this.whoChecklistService.getCheckListQuestions().subscribe(questions => {

      this.originalQuestions = questions.filter(q => q.id !== '29').sort((a, b) => {
        const parseId = (id: string) => {
          const match = id.match(/^(\d+)([a-zA-Z]*)$/);
          if (!match) return [0, ''];
          return [parseInt(match[1], 10), match[2] || ''];
        };
        const [numA, suffixA] = parseId(a.id);
        const [numB, suffixB] = parseId(b.id);

        if (numA !== numB) {
          return Number(numA) - Number(numB);
        }
        return String(suffixA).localeCompare(String(suffixB));
      });

      // Initialize working questions array as a copy of original
      this.questions = [...this.originalQuestions];

      // Filter out conditional questions that shouldn't be shown
      this.filterConditionalQuestions();

      // Calculate progress based on saved answers
      this.calculateProgressFromSavedAnswers();

      // Initialize the current question and its options
      this.updateCurrentQuestion();
    });
  }

  private calculateProgressFromSavedAnswers(): void {
    let answeredCount = 0;
    let hasAnyAnswers = false;

    // Check if we have any valid questions to process
    if (!this.questions || this.questions.length === 0) {
      this.completedStep = 0;
      this.selectedStep = 1;
      return;
    }

    for (let i = 0; i < this.questions.length; i++) {
      const question = this.questions[i];
      const propertyName = getWhoChecklistPropertyName(question.id);
      let existingValue = (this.whoChecklist as any)[propertyName];

      // If not found, try different case variations
      if (existingValue === null || existingValue === undefined) {

        var variation = `q${question.id}Options`; // Use the standard property name
        const value = (this.whoChecklist as any)[variation];
        if (value !== null && value !== undefined) {
          existingValue = value;
          break;
        }
      }

      // Check for valid answers - need to handle enum values (including 0) and arrays
      const hasAnswer = existingValue !== null &&
        existingValue !== undefined &&
        existingValue !== '' &&
        !(Array.isArray(existingValue) && existingValue.length === 0);

      if (hasAnswer) {
        answeredCount = i + 1; // Update completed step to this question
        hasAnyAnswers = true;
      } else {
        // If we find an unanswered question, stop counting
        break;
      }
    }

    // Update the completed step and selected step based on progress
    this.completedStep = answeredCount;

    // If no answers were found, start from the beginning
    if (!hasAnyAnswers) {
      this.selectedStep = 1;
      this.completedStep = 0;
    } else {
      // If user was at a specific step, keep that, otherwise start from the next unanswered question
      if (this.selectedStep <= 1 || this.selectedStep <= this.completedStep) {
        this.selectedStep = Math.min(this.completedStep + 1, this.questions.length);
      }
    }

    // Ensure selectedStep is within bounds
    if (this.selectedStep > this.questions.length) {
      this.selectedStep = this.questions.length;
    }
    if (this.selectedStep < 1) {
      this.selectedStep = 1;
    }
  }

  private filterConditionalQuestions(): void {

    // Check if question 6 has been answered
    const q6PropertyName = getWhoChecklistPropertyName('6');
    const q6Value = (this.whoChecklist as any)[q6PropertyName];

    // Only filter if question 6 has been answered
    if (q6Value !== null && q6Value !== undefined) {
      this.questions = this.originalQuestions.filter(question => {
        const shouldShow = shouldShowQuestion(question.id, this.whoChecklist);
        return shouldShow;
      });
    } else {
      this.questions = this.originalQuestions;
    }

    // Update totalSteps to match the filtered questions count
    // This ensures progress calculation uses the correct total based on conditional question visibility
    this.totalSteps = this.questions.length;
  }

  ngOnDestroy(): void {
    // Check for unsaved changes before the component is destroyed
    if (this.hasChanges) {
      // Try to save changes synchronously (fire-and-forget)
      const currentDoctorIds = this.selectedDoctors.map(doctor => doctor.id);
      const currentAnaesthetistIds = this.selectedAnaesthetists.map(anaesthetist => anaesthetist.id);

      this.theatrePatient.DoctorIds = currentDoctorIds;
      this.theatrePatient.AnaesthetistIds = currentAnaesthetistIds;

      // Fire-and-forget save attempt
      this.theatrePatientStorageService.updatePatient(this.theatrePatient)
        .then(() => console.log('Emergency save successful'))
        .catch(error => console.error('Emergency save failed:', error));
    }
  }

  onCommentInputFocused(value: boolean) {}

  isCustomKeyboardVisible(): boolean {
    return false;
  }

  public customKeyboardInput(inputText: string) {}

}